(() => {
  if (
    customElements.get('cart-freight-volume') &&
    customElements.get('cart-pickup-only-warning') &&
    customElements.get('cart-state-restriction-warning')
  ) return;

  const SP_K1_VALUE = 0.5;
  const ROUNDING_INCREMENT_K1 = 0.5;
  const ALLOWED_LOOSE_K1_VALUES = [0.5, 1];
  const WHOLE_K1_PROVINCES = new Set(['WA', 'NT']);
  let cartFooterRefreshRequest;

  function positiveNumber(value) {
    if (value === null || value === undefined || value === '') return null;

    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function roundUpToHalfK1(value) {
    return Math.ceil((value - Number.EPSILON) / ROUNDING_INCREMENT_K1) * ROUNDING_INCREMENT_K1;
  }

  function formatK1(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  function requiresWholeK1(province) {
    return WHOLE_K1_PROVINCES.has(String(province ?? '').trim().toUpperCase());
  }

  function isWholeK1(value) {
    return Math.abs(value - Math.round(value)) < Number.EPSILON * 10;
  }

  function calculateCartK1(items) {
    let standardK1 = 0;
    let hasPickupOnlyItem = false;
    const loosePools = new Map();

    items.forEach((item) => {
      const freightBehaviour = String(item.freight_behaviour ?? 'Standard').trim().toLowerCase();

      if (freightBehaviour === 'pickup only') {
        hasPickupOnlyItem = true;
        return;
      }

      const quantity = positiveNumber(item.quantity);
      const spCapacity = positiveNumber(item.sp_tray_qty);
      const k1Capacity = positiveNumber(item.k1_tray_qty);
      const looseGroup = String(item.loose_group ?? '').trim();
      const looseCapacity = positiveNumber(item.loose_capacity);
      const looseK1Value = positiveNumber(item.loose_k1_value);
      const isLooseProduct = Boolean(item.loose_group || item.loose_capacity || item.loose_k1_value);

      if (!quantity) {
        throw new Error(`Invalid quantity for ${item.name}`);
      }

      if (isLooseProduct) {
        if (!looseGroup || !looseCapacity || !looseK1Value) {
          throw new Error(`Incomplete loose freight data for ${item.name}`);
        }

        if (!ALLOWED_LOOSE_K1_VALUES.includes(looseK1Value)) {
          throw new Error(`Invalid loose K1 value for ${item.name}`);
        }

        const rawK1 = (quantity / looseCapacity) * looseK1Value;
        const pool = loosePools.get(looseGroup) ?? {
          rawK1: 0,
          k1RequiredRaw: 0,
        };

        pool.rawK1 += rawK1;
        if (looseK1Value === 1) pool.k1RequiredRaw += rawK1;
        loosePools.set(looseGroup, pool);
        return;
      }

      if (k1Capacity) {
        standardK1 += quantity / k1Capacity;
        return;
      }

      if (spCapacity) {
        standardK1 += (quantity / spCapacity) * SP_K1_VALUE;
        return;
      }

      throw new Error(`Missing SP/K1 tray capacity for ${item.name}`);
    });

    let looseK1 = 0;

    loosePools.forEach((pool) => {
      const minimumWholeK1 = Math.ceil(pool.k1RequiredRaw - Number.EPSILON);
      const allOccupancyToHalfK1 = roundUpToHalfK1(pool.rawK1);
      looseK1 += Math.max(minimumWholeK1, allOccupancyToHalfK1);
    });

    return {
      hasPickupOnlyItem,
      roundedK1: roundUpToHalfK1(standardK1 + looseK1),
    };
  }

  function isMinimumCheckoutLocked({
    hasPickupOnlyItem,
    roundedK1,
    minimumK1,
    pickupMinimumMet,
    selectedProvince,
  }) {
    const deliveryMinimumMet =
      !hasPickupOnlyItem && minimumK1 !== null && roundedK1 >= minimumK1;
    const wholeBoxRequirementMet =
      !requiresWholeK1(selectedProvince) || isWholeK1(roundedK1);

    return !wholeBoxRequirementMet || (!deliveryMinimumMet && !pickupMinimumMet);
  }

  async function fetchCurrentCartFooter() {
    if (!cartFooterRefreshRequest) {
      const cartUrl = window.routes?.cart_url ?? '/cart';

      cartFooterRefreshRequest = fetch(`${cartUrl}?section_id=main-cart-footer`)
        .then((response) => {
          if (!response.ok) throw new Error('Unable to refresh cart information');
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .finally(() => {
          cartFooterRefreshRequest = undefined;
        });
    }

    return cartFooterRefreshRequest;
  }

  function updateCheckoutControls() {
    const checkoutButtons = [
      {
        button: document.getElementById('checkout'),
        cartContainer: document.getElementById('main-cart-footer'),
      },
      {
        button: document.getElementById('CartDrawer-Checkout'),
        cartContainer: document.querySelector('cart-drawer'),
      },
    ];

    checkoutButtons.forEach(({ button, cartContainer }) => {
      if (!button) return;

      const cartEmpty = cartContainer?.classList.contains('is-empty') ?? false;
      const stateWarning = cartContainer?.querySelector('cart-state-restriction-warning');
      const freightVolume = cartContainer?.querySelector('cart-freight-volume');
      const stateRestrictionLocked =
        !stateWarning ||
        stateWarning.dataset.verificationPending === 'true' ||
        stateWarning.dataset.checkoutLocked !== 'false';
      const minimumLocked =
        !freightVolume ||
        freightVolume.dataset.minimumVerificationPending === 'true' ||
        freightVolume.dataset.minimumCheckoutLocked !== 'false';
      const disabled = cartEmpty || stateRestrictionLocked || minimumLocked;

      button.disabled = disabled;
      button.toggleAttribute('aria-disabled', disabled);
      button.toggleAttribute('data-state-restriction-locked', stateRestrictionLocked);
      button.toggleAttribute('data-minimum-locked', minimumLocked);
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((container) => {
      const checkoutButton = document.getElementById('checkout');
      container.hidden = checkoutButton?.disabled ?? true;
    });
  }

  class CartFreightVolume extends HTMLElement {
    connectedCallback() {
      this.renderCalculation();
      this.boundRefresh = this.refresh.bind(this);
      document.addEventListener('paradisia:cart-updated', this.boundRefresh);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundRefresh);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('paradisia:cart-updated', this.boundRefresh);
      this.unsubscribeCartUpdate?.();
    }

    getItems() {
      const dataElement = this.querySelector('[data-cart-freight-items]');
      return dataElement ? JSON.parse(dataElement.textContent) : [];
    }

    renderCalculation() {
      const currentElement = this.querySelector('.cart-freight-volume__current');
      const pickupNote = this.querySelector('.cart-freight-volume__pickup-note');
      const deliveryStatus = this.querySelector('[data-delivery-minimum-status]');
      const wholeK1Warning = this.querySelector('[data-whole-k1-warning]');

      if (!currentElement) {
        this.dataset.minimumCheckoutLocked = 'true';
        this.dataset.minimumVerificationPending = 'false';
        updateCheckoutControls();
        return;
      }

      try {
        const result = calculateCartK1(this.getItems());
        const minimumK1 = positiveNumber(this.dataset.minimumK1);
        const pickupMinimumMet = this.dataset.pickupMinimumMet === 'true';
        const selectedProvince = this.dataset.selectedProvince;
        const wholeBoxRequirementMet =
          !requiresWholeK1(selectedProvince) || isWholeK1(result.roundedK1);
        currentElement.textContent = `${formatK1(result.roundedK1)} K1`;

        if (deliveryStatus) {
          const minimumMet = minimumK1 !== null && result.roundedK1 >= minimumK1;
          deliveryStatus.textContent = 'Minimum not met';
          deliveryStatus.hidden = minimumMet;
        }

        if (pickupNote) pickupNote.hidden = !result.hasPickupOnlyItem;
        if (wholeK1Warning) wholeK1Warning.hidden = wholeBoxRequirementMet;
        this.dataset.minimumCheckoutLocked = String(
          isMinimumCheckoutLocked({
            hasPickupOnlyItem: result.hasPickupOnlyItem,
            roundedK1: result.roundedK1,
            minimumK1,
            pickupMinimumMet,
            selectedProvince,
          })
        );
        this.dataset.minimumVerificationPending = 'false';
        this.removeAttribute('data-calculation-error');
        updateCheckoutControls();
      } catch (error) {
        currentElement.textContent = 'Unavailable';
        if (wholeK1Warning) wholeK1Warning.hidden = true;
        this.dataset.minimumCheckoutLocked = 'true';
        this.dataset.minimumVerificationPending = 'false';
        this.setAttribute('data-calculation-error', '');
        updateCheckoutControls();
        console.warn('Paradisia cart freight volume:', error.message);
      }
    }

    async refresh() {
      this.dataset.minimumVerificationPending = 'true';
      updateCheckoutControls();

      try {
        const cartFooter = await fetchCurrentCartFooter();
        const source = cartFooter.querySelector('cart-freight-volume');

        if (!source) throw new Error('Cart freight volume section was not returned');
        this.dataset.minimumK1 = source.dataset.minimumK1;
        this.dataset.pickupMinimumMet = source.dataset.pickupMinimumMet;
        this.dataset.selectedProvince = source.dataset.selectedProvince;

        if (this.dataset.context === 'collection') {
          const sourceItems = source.querySelector('[data-cart-freight-items]');
          const currentItems = this.querySelector('[data-cart-freight-items]');

          if (sourceItems && currentItems) {
            currentItems.textContent = sourceItems.textContent;
          }
        } else {
          this.innerHTML = source.innerHTML;
        }

        this.renderCalculation();
      } catch (error) {
        this.dataset.minimumVerificationPending = 'false';
        this.dataset.minimumCheckoutLocked = 'true';
        updateCheckoutControls();
        console.warn('Paradisia cart freight volume refresh:', error.message);
      }
    }
  }

  class CartPickupOnlyWarning extends HTMLElement {
    connectedCallback() {
      this.boundRefresh = this.refresh.bind(this);
      document.addEventListener('paradisia:cart-updated', this.boundRefresh);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundRefresh);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('paradisia:cart-updated', this.boundRefresh);
      this.unsubscribeCartUpdate?.();
    }

    async refresh() {
      try {
        const cartFooter = await fetchCurrentCartFooter();
        const source = cartFooter.querySelector('cart-pickup-only-warning');

        if (!source) throw new Error('Pickup Only warning section was not returned');
        this.hidden = source.hidden;
        this.innerHTML = source.innerHTML;
      } catch (error) {
        console.warn('Paradisia Pickup Only warning refresh:', error.message);
      }
    }
  }

  class CartStateRestrictionWarning extends HTMLElement {
    connectedCallback() {
      this.applyCheckoutState();
      this.boundRefresh = this.refresh.bind(this);
      document.addEventListener('paradisia:cart-updated', this.boundRefresh);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundRefresh);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('paradisia:cart-updated', this.boundRefresh);
      this.unsubscribeCartUpdate?.();
    }

    isCheckoutLocked() {
      return this.dataset.checkoutLocked === 'true';
    }

    applyCheckoutState() {
      updateCheckoutControls();
    }

    showRefreshError() {
      this.dataset.checkoutLocked = 'true';
      this.dataset.verificationPending = 'false';
      this.hidden = false;
      this.innerHTML = `
        <div class="cart-information-card cart-state-restriction-warning__card" role="alert">
          <strong>Cart check unavailable</strong>
          <p>Refresh the page or select your company location again before checking out.</p>
        </div>
      `;
      this.applyCheckoutState();
    }

    async refresh() {
      this.dataset.verificationPending = 'true';
      updateCheckoutControls();

      try {
        const cartFooter = await fetchCurrentCartFooter();
        const source = cartFooter.querySelector('cart-state-restriction-warning');

        if (!source) throw new Error('Cart state restriction section was not returned');
        this.dataset.checkoutLocked = source.dataset.checkoutLocked;
        this.dataset.verificationPending = 'false';
        this.hidden = source.hidden;
        this.innerHTML = source.innerHTML;
        this.applyCheckoutState();
      } catch (error) {
        this.showRefreshError();
        console.warn('Paradisia cart state restriction refresh:', error.message);
      }
    }
  }

  if (!customElements.get('cart-freight-volume')) {
    customElements.define('cart-freight-volume', CartFreightVolume);
  }

  if (!customElements.get('cart-pickup-only-warning')) {
    customElements.define('cart-pickup-only-warning', CartPickupOnlyWarning);
  }

  if (!customElements.get('cart-state-restriction-warning')) {
    customElements.define('cart-state-restriction-warning', CartStateRestrictionWarning);
  }
})();
